/**
 * @file date-picker/date-picker.tsx
 *
 * DatePicker — calendar UI component (excluding dialog wrapper).
 * Equivalent to `DatePicker` composable in Android Compose Material3.
 *
 * Layout:
 *   CalendarHeader (title, headline, month nav)
 *   ↓
 *   CalendarGrid | YearPickerGrid (AnimatePresence toggle)
 *   ↓
 *   DatePickerInput (when displayMode === 'input')
 *
 * @see https://m3.material.io/components/date-pickers/overview
 */

import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { PICKER_SELECTOR_ANIM } from "../shared/animation-tokens";
import {
	formatDate,
	getNavigableMonth,
	isMonthNavigable,
} from "../shared/calendar-model";
import type { DatePickerProps } from "./date-picker.types";
import { DatePickerInput } from "./date-picker-input";
import { CalendarGrid } from "./subcomponents/calendar-grid";
import {
	CalendarHeader,
	type CalendarSelector,
} from "./subcomponents/calendar-header";
import { MonthPickerList } from "./subcomponents/month-picker-list";
import { YearPickerList } from "./subcomponents/year-picker-list";

/** Format UTC ms → "MM/DD/YYYY" for input sync */
const formatInputDate = (ms: number): string =>
	new Intl.DateTimeFormat("en-US", {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
		timeZone: "UTC",
	}).format(new Date(ms));

/**
 * DatePicker calendar UI.
 *
 * @example
 * ```tsx
 * const state = useDatePickerState({ locale: { locale: 'en-US' } });
 *
 * <DatePicker
 *   state={state}
 *   showModeToggle
 *   title="Select date"
 * />
 * ```
 */

export const DatePicker: React.FC<DatePickerProps> = ({
	state,
	showModeToggle = true,
	title,
	headline,
	className,
}) => {
	const {
		selectedDateMs,
		displayedMonthMs,
		displayMode,
		yearRange,
		locale,
		selectableDates,
		selectDate,
		navigateMonth,
		navigateToMonth,
		setDisplayMode,
	} = state;

	const [activeSelector, setActiveSelector] =
		React.useState<CalendarSelector>(null);

	// ── Navigation direction tracking ────────────────────────────────────────────
	const [slideDirection, setSlideDirection] = React.useState<
		"prev" | "next" | null
	>(null);

	const handlePrev = React.useCallback(() => {
		setSlideDirection("prev");
		navigateMonth("prev");
	}, [navigateMonth]);

	const handleNext = React.useCallback(() => {
		setSlideDirection("next");
		navigateMonth("next");
	}, [navigateMonth]);

	const handleYearSelect = React.useCallback(
		(year: number) => {
			const d = new Date(displayedMonthMs);
			navigateToMonth(year, d.getUTCMonth());
			setActiveSelector(null);
		},
		[displayedMonthMs, navigateToMonth],
	);

	const handleMonthSelect = React.useCallback(
		(month: number) => {
			const d = new Date(displayedMonthMs);
			navigateToMonth(d.getUTCFullYear(), month);
			setActiveSelector(null);
		},
		[displayedMonthMs, navigateToMonth],
	);

	// ── Input mode state ─────────────────────────────────────────────────────────
	const [inputValue, setInputValue] = React.useState(() =>
		selectedDateMs !== null ? formatInputDate(selectedDateMs) : "",
	);
	const [inputError, setInputError] = React.useState<string | undefined>();

	const handleInputCommit = React.useCallback(
		(ms: number | null) => {
			if (ms === null && inputValue.length > 0) {
				setInputError("Invalid date");
			} else {
				setInputError(undefined);
				selectDate(ms);
			}
		},
		[inputValue, selectDate],
	);

	// ── Default title / headline ─────────────────────────────────────────────────
	const resolvedTitle = title ?? "Select date";
	const resolvedHeadline =
		headline ??
		(selectedDateMs !== null
			? formatDate(selectedDateMs, locale, {
					weekday: "short",
					month: "short",
					day: "numeric",
					timeZone: "UTC",
				})
			: "—");

	// ── Current year ─────────────────────────────────────────────────────────────
	const currentYear = new Date(displayedMonthMs).getUTCFullYear();
	const currentMonth = new Date(displayedMonthMs).getUTCMonth();
	const computedAriaLabel =
		typeof resolvedTitle === "string" ? resolvedTitle : "Date picker";
	const canNavigatePrev =
		getNavigableMonth(displayedMonthMs, "prev", yearRange, selectableDates) !==
		null;
	const canNavigateNext =
		getNavigableMonth(displayedMonthMs, "next", yearRange, selectableDates) !==
		null;
	const canNavigatePreviousYear = isMonthNavigable(
		currentYear - 1,
		yearRange,
		selectableDates,
	);
	const canNavigateNextYear = isMonthNavigable(
		currentYear + 1,
		yearRange,
		selectableDates,
	);

	return (
		<fieldset
			className={cn("flex flex-col select-none", className)}
			style={{ width: "min(100%, 360px)" }}
			aria-label={computedAriaLabel}
		>
			<legend className="sr-only">{computedAriaLabel}</legend>
			{/* Header */}
			<CalendarHeader
				displayedMonthMs={displayedMonthMs}
				activeSelector={activeSelector}
				displayMode={displayMode}
				showModeToggle={showModeToggle}
				title={resolvedTitle}
				headline={resolvedHeadline}
				locale={locale}
				canNavigatePrev={canNavigatePrev}
				canNavigateNext={canNavigateNext}
				canNavigatePreviousYear={canNavigatePreviousYear}
				canNavigateNextYear={canNavigateNextYear}
				onPrev={handlePrev}
				onNext={handleNext}
				onPreviousYear={() => navigateToMonth(currentYear - 1, currentMonth)}
				onNextYear={() => navigateToMonth(currentYear + 1, currentMonth)}
				onToggleSelector={(selector) =>
					setActiveSelector((active) => (active === selector ? null : selector))
				}
				onToggleMode={() => {
					setDisplayMode(displayMode === "picker" ? "input" : "picker");
					setActiveSelector(null);
					setInputError(undefined);
				}}
			/>

			{/* Body */}
			<div
				className="relative flex-1 overflow-hidden"
				style={{ minHeight: "296px" }}
			>
				<AnimatePresence mode="wait">
					{displayMode === "input" ? (
						// Input mode
						<DatePickerInput
							key="input-mode"
							value={inputValue}
							onChange={setInputValue}
							onCommit={handleInputCommit}
							error={inputError}
							locale={locale.locale}
						/>
					) : activeSelector === "month" ? (
						<m.div
							id="date-picker-selection"
							key="month-list"
							initial={PICKER_SELECTOR_ANIM.initial}
							animate={PICKER_SELECTOR_ANIM.animate}
							exit={PICKER_SELECTOR_ANIM.exit}
							className="absolute inset-0"
						>
							<MonthPickerList
								selectedMonth={currentMonth}
								locale={locale}
								onMonthSelect={handleMonthSelect}
							/>
						</m.div>
					) : activeSelector === "year" ? (
						<m.div
							id="date-picker-selection"
							key="year-list"
							initial={PICKER_SELECTOR_ANIM.initial}
							animate={PICKER_SELECTOR_ANIM.animate}
							exit={PICKER_SELECTOR_ANIM.exit}
							className="absolute inset-0"
						>
							<YearPickerList
								selectedYear={currentYear}
								yearRange={yearRange}
								selectableDates={selectableDates}
								onYearSelect={handleYearSelect}
							/>
						</m.div>
					) : (
						// Calendar grid
						<CalendarGrid
							key="calendar"
							displayedMonthMs={displayedMonthMs}
							slideDirection={slideDirection}
							locale={locale}
							selectableDates={selectableDates}
							selectedMs={selectedDateMs}
							isRangePicker={false}
							onDaySelect={(ms) => {
								selectDate(ms);
								setInputValue(formatInputDate(ms));
							}}
						/>
					)}
				</AnimatePresence>
			</div>
		</fieldset>
	);
};

DatePicker.displayName = "DatePicker";
